import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { QuestionContentModel } from '@protos/content_management/content.db_pb';
import {
  PAL_CHALLENGE,
  PAL_DIAGNOSTIC,
  PAL_PRACTICE,
  PAL_VIEW_CONTENT,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Learnometer } from '../Learnometer';
import {
  getConceptMasteryApi,
  IConceptMasteryResponse,
} from '../../api/learners';
import WhyQuestionPopup from '../WhyQuestionPopup';
import type { WhyQuestionPopupContent } from '../WhyQuestionPopup';

export const difficultyColors: Record<
  string,
  { bg: string; color: string; dot: string }
> = {
  Easy: { bg: '#f0fdf4', color: '#16a34a', dot: '#22c55e' },
  Medium: { bg: '#fffbeb', color: '#d97706', dot: '#f59e0b' },
  Hard: { bg: '#fef2f2', color: '#dc2626', dot: '#ef4444' },
};

export type PalMode = 'warmup' | 'learning' | 'practice' | 'challenge';

export const palModes: {
  key: PalMode;
  icon: string;
  label: string;
  /** i18n key for {@link label}. */
  labelKey: string;
  description: string;
  /** i18n key for {@link description}. */
  descriptionKey: string;
}[] = [
  {
    key: 'practice',
    icon: '✏️',
    label: 'Practice Mode',
    labelKey: 'PAL_MODE_PRACTICE_LABEL',
    description: 'No timer, learn at your pace',
    descriptionKey: 'PAL_MODE_PRACTICE_DESC',
  },
  {
    key: 'warmup',
    icon: '☀️',
    label: 'Warm-up',
    labelKey: 'PAL_MODE_WARMUP_LABEL',
    description: 'Guided practice with coaching tips',
    descriptionKey: 'PAL_MODE_WARMUP_DESC',
  },
  {
    key: 'learning',
    icon: '📚',
    label: 'Learning Mode',
    labelKey: 'PAL_MODE_LEARNING_LABEL',
    description: 'Watch videos & read resources',
    descriptionKey: 'PAL_MODE_LEARNING_DESC',
  },
  {
    key: 'challenge',
    icon: '⚡',
    label: 'Challenge Mode',
    labelKey: 'PAL_MODE_CHALLENGE_LABEL',
    description: 'Timed test, real assessment',
    descriptionKey: 'PAL_MODE_CHALLENGE_DESC',
  },
];

export const questionTypeTitleFromQuestionContent = (
  QuestionContent?: QuestionContentModel
) => {
  if (!QuestionContent) {
    return undefined;
  }
  const quesType = QuestionContent.model.case;
  // Returns an i18n key; callers should pass it through `t()`.
  switch (quesType) {
    case 'mcqSingleContentModel':
      return 'PAL_QTYPE_MCQ_SINGLE';
    case 'mcqMultipleContentModel':
      return 'PAL_QTYPE_MCQ_MULTIPLE';
    case 'tfContentModel':
      return 'PAL_QTYPE_TRUE_FALSE';
    case 'fibContentModel':
      return 'PAL_QTYPE_FIB';
    case 'veryShortQuesContentModel':
      return 'PAL_QTYPE_VERY_SHORT';
    case 'shortQuesContentModel':
      return 'PAL_QTYPE_SHORT';
    case 'longQuesContentModel':
      return 'PAL_QTYPE_LONG';
    case 'cbqQuesContentModel':
      return 'PAL_QTYPE_COMPETENCY';
    case 'arrangeQuestionContentModel':
      return 'PAL_QTYPE_ARRANGE';
    case 'mtfMultipleQuesContentModel':
      return 'PAL_QTYPE_MTF_MULTIPLE';
    case 'mtfSingleQuesContentModel':
      return 'PAL_QTYPE_MTF_SINGLE';
    default:
      return undefined;
  }
};

export const getPalModeFromPathName = (pathName: string): PalMode => {
  if (pathName.includes(PAL_PRACTICE)) {
    return 'practice';
  } else if (pathName.includes(PAL_DIAGNOSTIC)) {
    return 'warmup';
  } else if (pathName.includes(PAL_VIEW_CONTENT)) {
    return 'learning';
  } else if (pathName.includes(PAL_CHALLENGE)) {
    return 'challenge';
  }
  return 'practice';
};

type Props = {
  children: ReactNode;
  rightPanel?: ReactNode;
  conceptName?: string;
  streak?: number;
};
export const PalAssessmentWrapper = ({
  children,
  rightPanel,
  conceptName,
  streak,
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const palMode = getPalModeFromPathName(location.pathname);
  const currentPalMode =
    palModes.find((mode) => mode.key === palMode) || palModes[0];

  const [showWhyQuestionPopup, setShowWhyQuestionPopup] = useState(false);

  const whyQuestionPopupContent: WhyQuestionPopupContent = {
    headerIcon: '🎯',
    headerTitle: 'Why This Question?',
    weakAreasTitle: 'Targeting Your Weak Areas',
    weakAreasDescription:
      'Based on your recent performance, we noticed you struggled with these concepts:',
    weakAreas: [
      { label: 'Motion & Friction', value: '47%' },
      { label: 'Forces & Drag', value: '50%' },
    ],
    foundationTitle: 'Building Your Foundation',
    foundationDescription:
      'This question helps strengthen your understanding of:',
    foundationPoints: [
      {
        title: 'Shape & Drag Relationship',
        desc: 'Essential for aerodynamics topics ahead',
      },
      { title: 'Fluid Properties', desc: 'Foundation for pressure & buoyancy' },
      {
        title: "Speed's Effect on Friction",
        desc: 'Key for energy & momentum concepts',
      },
    ],
    nextTitle: "What's Coming Next",
    nextDescription: 'Mastering this will prepare you for:',
    nextTopics: [
      { label: 'Aerodynamics & Flight', icon: '✈️' },
      { label: 'Fluid Mechanics', icon: '🌊' },
      { label: 'Energy Conservation', icon: '⚡' },
    ],
    encouragementIcon: '💪',
    encouragementText:
      "You've got this! Every question is a step toward mastery.",
  };

  const renderHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          px: 3,
          // background:
          //   'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
          // borderBottom: '1px solid #e8e8e8',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {/* Concept focus tag */}
        {conceptName && (
          <Chip
            icon={<span style={{ fontSize: 14 }}>🎓</span>}
            label={t('PAL_WRAP_FOCUSING_ON', { conceptName })}
            size="small"
            sx={{
              bgcolor: 'white',
              borderRadius: '15px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              fontSize: 14,
              fontWeight: 500,
              padding: '6px 8px',
              height: 'unset',
            }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            flexGrow: 1,
          }}
        >
          {/* Mode Selector */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Button
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '15px',
                boxShadow: '0 2px 8px rgba(245,87,108,0.3)',
                fontSize: 14,
                fontWeight: 500,
                padding: '2px 8px',
                height: 'unset',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                textTransform: 'none',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(245,87,108,0.4)',
                },
              }}
            >
              <span>{currentPalMode.icon}</span>
              <span>{t(currentPalMode.labelKey)}</span>
            </Button>
            {/* <Button
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                borderRadius: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                fontSize: 13,
                fontWeight: 500,
                padding: '4px 12px',
                height: 'unset',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                color: '#4f46e5',
                cursor: 'pointer',
                textTransform: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': {
                  background: '#ffffff',
                  border: '1px solid #6366f1',
                  boxShadow: '0 2px 8px rgba(99,102,241,0.15)',
                },
              }}
              onClick={() => setShowWhyQuestionPopup(true)}
            >
              <span>❓</span>
              <span>why this question?</span>
            </Button> */}
          </Box>

          {/* Streak badge - hidden initially, shown when streak > 0 */}
          {!!streak && streak >= 1 && (
            <Chip
              icon={<span style={{ fontSize: 12 }}>🔥</span>}
              label={t('PAL_WRAP_STREAK', { streak })}
              size="small"
              sx={{
                background:
                  'linear-gradient(135deg, #ff6b6b, #ff8e53, #ffa500)',
                color: 'white',
                borderRadius: '15px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                fontSize: 14,
                fontWeight: 500,
                padding: '5px 8px',
                height: 'unset',
                visibility: 1 ? 'visible' : 'hidden',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        px: { xs: '20px', md: '30px' },
        py: { xs: '10px', md: '20px' },
        height: { xs: 'auto', md: 'calc(100% - 40px)' },
        minHeight: { xs: 'calc(100% - 20px)', md: 'auto' },
        gap: '20px',
        overflowY: { xs: 'auto', md: 'auto' },
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'auto', md: '100%' },
          flex: { xs: '1 1 auto', md: rightPanel ? '0 1 75%' : '1 1 0' },
          maxWidth: { xs: '100%', md: rightPanel ? '75%' : '100%' },
          minWidth: 0,
          gap: '15px',
        }}
      >
        {renderHeader()}
        {children}
      </Box>
      {/* Right Panel */}
      {rightPanel && (
        <Box
          sx={{
            flex: { xs: '0 0 auto', md: '1 1 0' },
            maxWidth: { xs: '100%', md: '25%' },
            minWidth: 0,
          }}
        >
          {rightPanel}
        </Box>
      )}
      <WhyQuestionPopup
        open={showWhyQuestionPopup}
        onClose={() => setShowWhyQuestionPopup(false)}
        content={whyQuestionPopupContent}
      />
    </Box>
  );
};
